import { Link } from "react-router-dom";
import { ArrowLeft, Clock, CalendarCheck, MessageCircle } from "lucide-react";
import InternalNav from "@/components/InternalNav";
import InlineText from "@/components/InlineText";
import InlineList from "@/components/InlineList";
import { MEDIA } from "@/content/media";
import { useContent } from "@/lib/ContentProvider";

/**
 * MenuPetitDejeuner — page dédiée au menu du petit-déjeuner.
 * Accessible via le bouton "Découvrir le menu" sur /experiences.
 * Ne remplace pas /experiences/petit-dejeuner (page immersive).
 */

const MENU_DEFAULT = [
  ["Œufs brouillés", "Au choix : bacon ou fromage (à préciser à l'avance)"],
  ["Pain toasté", "Beurre maison · nature ou thym"],
  ["Confitures maison", "Au gré des saisons"],
  ["Fruits frais", "De saison"],
  ["Jus d'orange", "Pressé"],
  ["Café", "Ou thé sur demande"],
  ["Pancakes maison", "Chocolat"],
];

const WA_TEMPLATE = "Bonjour, j'ai déjà réservé mon séjour et je souhaite ajouter le petit déjeuner.";

export default function MenuPetitDejeuner() {
  const { mediaUrl, buildWhatsAppUrl, content, saveText } = useContent();
  const cms = content?.menuPetitDejeuner || {};
  const val = (k, def) => (cms[k] !== undefined && cms[k] !== null && cms[k] !== "" ? cms[k] : def);
  const items = Array.isArray(cms.items) && cms.items.length ? cms.items : MENU_DEFAULT.map(([t, d]) => ({ title: t, desc: d }));

  return (
    <div data-testid="menu-petit-dejeuner-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-10 md:pb-14 bg-refuge-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link
            to="/experiences"
            data-testid="menu-back"
            className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} />{" "}
            <InlineText
              path="menuPetitDejeuner.backLabel"
              value={val("backLabel", "Retour aux expériences")}
              onSave={(v) => saveText("menuPetitDejeuner.backLabel", v)}
            />
          </Link>
          <InlineText
            as="p"
            path="menuPetitDejeuner.eyebrow"
            value={val("eyebrow", "Au refuge")}
            onSave={(v) => saveText("menuPetitDejeuner.eyebrow", v)}
            className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4"
          />
          <h1 className="font-heading text-4xl md:text-6xl font-light text-refuge-text mb-6 leading-[1.05]">
            <InlineText
              path="menuPetitDejeuner.title"
              value={val("title", "Le menu")}
              onSave={(v) => saveText("menuPetitDejeuner.title", v)}
            />{" "}
            <span className="italic">
              <InlineText
                path="menuPetitDejeuner.titleItalic"
                value={val("titleItalic", "du refuge")}
                onSave={(v) => saveText("menuPetitDejeuner.titleItalic", v)}
              />
            </span>
          </h1>
          <InlineText
            as="p"
            multiline
            path="menuPetitDejeuner.sub"
            value={val("sub", "Servi au refuge, dans un cadre calme et intime.")}
            onSave={(v) => saveText("menuPetitDejeuner.sub", v)}
            className="font-heading italic font-light text-refuge-soft/85 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
          />
          <div className="inline-flex items-center gap-3 mt-6 px-5 py-2.5 rounded-full bg-white border border-refuge-border/60 shadow-sm">
            <Clock size={14} className="text-refuge-ruby" strokeWidth={1.4} />
            <InlineText
              as="span"
              multiline
              path="menuPetitDejeuner.hours"
              value={val("hours", "De 9H à 10H, du lundi au samedi · pas de service le dimanche")}
              onSave={(v) => saveText("menuPetitDejeuner.hours", v)}
              className="font-body text-sm text-refuge-text"
            />
          </div>
        </div>
      </section>

      {/* Photo pleine largeur */}
      <section className="pb-16 md:pb-24 bg-refuge-bg">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div
            className="overflow-hidden rounded-2xl border border-refuge-border/60"
            style={{ boxShadow: "0 30px 60px -32px rgba(45,40,37,0.35)" }}
          >
            <img
              src={mediaUrl(MEDIA.experiences.petitDejeuner1)}
              alt="Petit déjeuner servi au Curieux Refuge"
              loading="eager"
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="pb-20 md:pb-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-16 md:pt-20">
          <InlineText
            as="p"
            path="menuPetitDejeuner.menuEyebrow"
            value={val("menuEyebrow", "À table")}
            onSave={(v) => saveText("menuPetitDejeuner.menuEyebrow", v)}
            className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-3"
          />
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text text-center mb-12 leading-tight">
            <InlineText
              path="menuPetitDejeuner.menuTitle"
              value={val("menuTitle", "Ce que vous trouverez")}
              onSave={(v) => saveText("menuPetitDejeuner.menuTitle", v)}
            />{" "}
            <span className="italic">
              <InlineText
                path="menuPetitDejeuner.menuTitleItalic"
                value={val("menuTitleItalic", "dans l'assiette")}
                onSave={(v) => saveText("menuPetitDejeuner.menuTitleItalic", v)}
              />
            </span>
          </h2>
          <InlineList
            items={items.map((it) => `${it.title} — ${it.desc}`)}
            path="menuPetitDejeuner.items"
            onSave={(arr) => {
              const parsed = arr.map((line) => {
                const [t, d] = line.split(/\s+—\s+|\s+-\s+/, 2);
                return { title: (t || line).trim(), desc: (d || "").trim() };
              });
              saveText("menuPetitDejeuner.items", parsed);
            }}
            wrapAs="div"
            wrapClassName="grid md:grid-cols-2 gap-x-12 gap-y-6"
            addLabel="Ajouter un plat"
            placeholder="Titre — description"
            renderItem={(text) => {
              const [t, d] = text.split(/\s+—\s+|\s+-\s+/, 2);
              return (
                <div className="pb-5 border-b border-dotted border-refuge-border/60">
                  <p className="font-heading text-xl md:text-2xl font-light text-refuge-text mb-1">{t || text}</p>
                  {d && <p className="font-body text-sm text-refuge-soft">{d}</p>}
                </div>
              );
            }}
          />
          <InlineText
            as="p"
            multiline
            path="menuPetitDejeuner.menuFooter"
            value={val("menuFooter", "Produits simples et frais, préparations maison au gré des saisons.")}
            onSave={(v) => saveText("menuPetitDejeuner.menuFooter", v)}
            className="font-body italic text-sm text-refuge-soft/80 mt-10 text-center max-w-xl mx-auto"
          />
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-refuge-ruby py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <InlineText
            as="p"
            path="menuPetitDejeuner.ctaEyebrow"
            value={val("ctaEyebrow", "Réserver")}
            onSave={(v) => saveText("menuPetitDejeuner.ctaEyebrow", v)}
            className="font-body text-xs tracking-[0.2em] uppercase text-refuge-rose mb-4"
          />
          <h3 className="font-heading text-3xl md:text-4xl font-light text-white mb-8 leading-tight">
            <InlineText
              path="menuPetitDejeuner.ctaTitle"
              value={val("ctaTitle", "Ajouter le petit déjeuner")}
              onSave={(v) => saveText("menuPetitDejeuner.ctaTitle", v)}
            />{" "}
            <span className="italic text-refuge-rose">
              <InlineText
                path="menuPetitDejeuner.ctaTitleItalic"
                value={val("ctaTitleItalic", "à votre séjour")}
                onSave={(v) => saveText("menuPetitDejeuner.ctaTitleItalic", v)}
              />
            </span>
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/reserver"
              data-testid="menu-cta-reserver"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-refuge-ruby font-body text-sm hover:bg-white/95 transition-all"
            >
              <CalendarCheck size={16} strokeWidth={1.3} />{" "}
              <InlineText
                path="menuPetitDejeuner.ctaReserverLabel"
                value={val("ctaReserverLabel", "Choisir mes dates")}
                onSave={(v) => saveText("menuPetitDejeuner.ctaReserverLabel", v)}
              />
            </Link>
            <a
              href={buildWhatsAppUrl(WA_TEMPLATE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white text-white font-body text-sm hover:bg-white hover:text-refuge-ruby transition-all"
            >
              <MessageCircle size={16} />{" "}
              <InlineText
                path="menuPetitDejeuner.ctaWhatsappLabel"
                value={val("ctaWhatsappLabel", "J'ai déjà réservé")}
                onSave={(v) => saveText("menuPetitDejeuner.ctaWhatsappLabel", v)}
              />
            </a>
          </div>
          <InlineText
            as="p"
            multiline
            path="menuPetitDejeuner.ctaFooter"
            value={val("ctaFooter", "Réservation à effectuer au minimum 96 heures (4 jours) avant votre arrivée.")}
            onSave={(v) => saveText("menuPetitDejeuner.ctaFooter", v)}
            className="font-body text-sm text-white/70 mt-6 max-w-md mx-auto leading-relaxed"
          />
        </div>
      </section>

      <div className="bg-refuge-bg py-10 text-center">
        <Link
          to="/experiences"
          data-testid="menu-back-bottom"
          className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour aux expériences
        </Link>
      </div>
    </div>
  );
}
